import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { permissionsAPI, usersAPI, branchesAPI, locationsAPI, rolesAPI } from '../lib/api';
import api from '../lib/api';
import { Shield, Users, KeyRound, Eye } from 'lucide-react';import { toast } from 'sonner';
import { defaultPerms } from './permissions/constants';
import { UsersTab } from './permissions/UsersTab';
import { RolesTab } from './permissions/RolesTab';
import { VisibilityMatrixTab } from './permissions/VisibilityMatrixTab';
import { UserPermissionDialog } from './permissions/UserPermissionDialog';
import { RoleEditorDialog } from './permissions/RoleEditorDialog';
import { NewUserDialog } from './permissions/NewUserDialog';

const initialUserPermForm = {
  role_id: '', branch_ids: [], location_ids: [], all_branches: false, all_locations: false,
};
const initialRoleForm = {
  name: '', description: '', scope_type: 'location', color: '#6B7280', permissions: defaultPerms(),
};
const initialNewUserForm = {
  email: '', password: '', first_name: '', last_name: '', role: 'employee',
};

export default function Permissions() {
  const navigate = useNavigate();
  const { startPreview, user: currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermForm, setUserPermForm] = useState(initialUserPermForm);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleForm, setRoleForm] = useState(initialRoleForm);

  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState(initialNewUserForm);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, permissionsRes, rolesRes, branchesRes, locationsRes] = await Promise.all([
        usersAPI.getAll(), permissionsAPI.getAll(), rolesAPI.getAll(),
        branchesAPI.getAll(), locationsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setPermissions(permissionsRes.data);
      setRoles(rolesRes.data);
      setBranches(branchesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ==================== Users ====================
  const handleDeleteUser = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error('Sie können sich nicht selbst löschen');
      return;
    }
    if (!window.confirm(`Benutzer "${u.first_name} ${u.last_name}" (${u.email}) wirklich endgültig löschen?\n\nDer Benutzer wird aus Login, Berechtigungen und Mitarbeiter-Verknüpfung entfernt.`)) return;
    try {
      const res = await usersAPI.delete(u.id);
      const d = res.data || {};
      toast.success(`Benutzer gelöscht (Berechtigungen: ${d.permissions_deleted || 0}, Mitarbeiter entkoppelt: ${d.employees_unlinked || 0})`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('Verwaiste Berechtigungs-Einträge (ohne zugehörigen Benutzer) endgültig entfernen?')) return;
    try {
      const res = await usersAPI.cleanupOrphanPermissions();
      const removed = res.data?.removed || 0;
      toast.success(removed > 0 ? `${removed} verwaiste Berechtigungen entfernt` : 'Keine verwaisten Berechtigungen gefunden');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Bereinigung');
    }
  };

  const openUserDialog = (user) => {
    const existingPerm = permissions.find((p) => p.user_id === user.id);
    setSelectedUser(user);
    setUserPermForm({
      role_id: existingPerm?.role_id || '',
      branch_ids: existingPerm?.branch_ids || [],
      location_ids: existingPerm?.location_ids || [],
      all_branches: existingPerm?.all_branches || false,
      all_locations: existingPerm?.all_locations || false,
    });
    setUserDialogOpen(true);
  };

  const handleSaveUserPerm = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await permissionsAPI.update(selectedUser.id, {
        user_id: selectedUser.id,
        role_id: userPermForm.role_id || null,
        branch_ids: userPermForm.branch_ids,
        location_ids: userPermForm.location_ids,
        all_branches: userPermForm.all_branches,
        all_locations: userPermForm.all_locations,
      });
      toast.success('Berechtigungen gespeichert');
      setUserDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Roles ====================
  const openRoleDialog = (role) => {
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || '',
        scope_type: role.scope_type,
        color: role.color,
        permissions: { ...defaultPerms(), ...(role.permissions || {}) },
      });
    } else {
      setSelectedRole(null);
      setRoleForm(initialRoleForm);
    }
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: roleForm.name,
        description: roleForm.description,
        scope_type: roleForm.scope_type,
        color: roleForm.color,
        permissions: roleForm.permissions,
      };
      if (selectedRole) {
        await rolesAPI.update(selectedRole.id, payload);
        toast.success('Rolle aktualisiert');
      } else {
        await rolesAPI.create(payload);
        toast.success('Rolle erstellt');
      }
      setRoleDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system) {
      toast.error('Systemrollen können nicht gelöscht werden');
      return;
    }
    const hasUsers = role.user_count > 0;
    const confirmMsg = hasUsers
      ? `Rolle "${role.name}" wird von ${role.user_count} Benutzer(n) verwendet.\n\nTrotzdem löschen? Die betroffenen Benutzer verlieren ihre Rollenzuweisung und müssen neu zugewiesen werden.`
      : `Rolle "${role.name}" wirklich löschen?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await rolesAPI.delete(role.id, { force: hasUsers });
      const unassigned = res.data?.users_unassigned || 0;
      toast.success(unassigned > 0 ? `Rolle gelöscht (${unassigned} Benutzer ohne Rolle)` : 'Rolle gelöscht');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Fehler beim Löschen');
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('Doppelte Rollen automatisch bereinigen?\n\nFür jeden Rollennamen wird die System-Variante behalten. Benutzer von Duplikaten werden auf die kanonische Rolle migriert.')) return;
    try {
      const res = await rolesAPI.cleanupDuplicates();
      const d = res.data || {};
      if ((d.removed_count || 0) === 0) {
        toast.success('Keine doppelten Rollen gefunden');
      } else {
        toast.success(`${d.removed_count} Duplikat(e) entfernt, ${d.migrated_users || 0} Benutzer migriert`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Bereinigung');
    }
  };

  const handlePreviewRole = (role) => {
    startPreview(role);
    setRoleDialogOpen(false);
    navigate('/dashboard');
  };

  // ==================== New User ====================
  const handleCreateUser = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.first_name || !newUserForm.last_name) {
      toast.error('Bitte fuellen Sie alle Pflichtfelder aus');
      return;
    }
    setCreatingUser(true);
    try {
      await api.post('/auth/register', newUserForm);
      toast.success('Benutzer erstellt');
      setNewUserDialogOpen(false);
      setNewUserForm(initialNewUserForm);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreatingUser(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="permissions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Rechtevergabe
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Benutzer, Rollen und Berechtigungsstufen
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
              <Users className="h-4 w-4" /> Benutzer
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="tab-roles">
              <KeyRound className="h-4 w-4" /> Rollen definieren
            </TabsTrigger>
            <TabsTrigger value="visibility" className="flex items-center gap-2" data-testid="tab-visibility">
              <Eye className="h-4 w-4" /> Sichtbarkeit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <UsersTab
              users={users}
              permissions={permissions}
              roles={roles}
              currentUser={currentUser}
              onEdit={openUserDialog}
              onDelete={handleDeleteUser}
              onCleanupOrphans={handleCleanupOrphans}
              onNewUser={() => setNewUserDialogOpen(true)}
            />
          </TabsContent>

          <TabsContent value="roles" className="mt-6">
            <RolesTab
              roles={roles}
              onEdit={openRoleDialog}
              onCleanupDuplicates={handleCleanupDuplicates}
              onPreview={(role) => { startPreview(role); navigate('/dashboard'); }}
            />
          </TabsContent>
          <TabsContent value="visibility" className="mt-6">
            <VisibilityMatrixTab roles={roles} />
          </TabsContent>
        </Tabs>

        <UserPermissionDialog
          open={userDialogOpen}
          onOpenChange={setUserDialogOpen}
          selectedUser={selectedUser}
          userPermForm={userPermForm}
          setUserPermForm={setUserPermForm}
          roles={roles}
          branches={branches}
          locations={locations}
          saving={saving}
          onSave={handleSaveUserPerm}
        />

        <RoleEditorDialog
          open={roleDialogOpen}
          onOpenChange={setRoleDialogOpen}
          selectedRole={selectedRole}
          roleForm={roleForm}
          setRoleForm={setRoleForm}
          saving={saving}
          onSave={handleSaveRole}
          onDelete={handleDeleteRole}
          onPreview={handlePreviewRole}
        />

        <NewUserDialog
          open={newUserDialogOpen}
          onOpenChange={setNewUserDialogOpen}
          form={newUserForm}
          setForm={setNewUserForm}
          creating={creatingUser}
          onSubmit={handleCreateUser}
        />
      </div>
    </DashboardLayout>
  );
}
